/** TypeScript types matching backend Pydantic response schemas for metrics. */

import type { DateRange } from "react-day-picker";

export interface ActiveUsersPoint {
  date: string;
  dau: number;
  wau: number;
  mau: number;
}

export interface ActiveUsersResponse {
  points: ActiveUsersPoint[];
  summary: {
    current_dau: number;
    current_wau: number;
    current_mau: number;
  };
}

export interface LoginFrequencyBucket {
  bucket: string;
  user_count: number;
}

export interface LoginFrequencyResponse {
  buckets: LoginFrequencyBucket[];
  total_users: number;
  avg_logins_per_user: number;
}

export interface RetentionCohort {
  cohort_date: string;
  cohort_size: number;
  week_1: number;
  week_2: number;
  week_4: number;
  week_8: number;
}

export interface UserRetentionResponse {
  cohorts: RetentionCohort[];
  avg_week_1_retention: number;
}

export interface PageUsageItem {
  page_path: string;
  visit_count: number;
  unique_visitors: number;
}

export interface PageUsageResponse {
  pages: PageUsageItem[];
  total_page_views: number;
}

export interface PageDwellTimeItem {
  page_path: string;
  avg_dwell_seconds: number;
  median_dwell_seconds: number;
  visit_count: number;
}

export interface PageDwellTimeResponse {
  pages: PageDwellTimeItem[];
  overall_avg_dwell_seconds: number;
}

export interface PlanDistributionItem {
  plan: string;
  label: string;
  user_count: number;
  is_paid: boolean;
}

export interface PlanDistributionResponse {
  plans: PlanDistributionItem[];
  total_users: number;
  trial_users: number;
  expired_trial_users: number;
  subscribed_users: number;
}

export type TimeRange = "7d" | "30d" | "90d";

/** Custom date range with ISO 8601 date strings (YYYY-MM-DD). */
export interface CustomDateRange {
  from: string;
  to: string;
}

/** Union: either a preset ("7d"/"30d"/"90d") or an explicit {from, to} pair. */
export type MetricsRange = TimeRange | CustomDateRange;

/** Type guard: true when the range is a custom {from, to} object. */
export function isCustomDateRange(range: MetricsRange): range is CustomDateRange {
  return typeof range === "object" && range !== null && "from" in range && "to" in range;
}

/** Serializes a MetricsRange into URL query params for the metrics API. */
export function rangeToQueryParams(range: MetricsRange): string {
  if (isCustomDateRange(range)) {
    return `from_date=${encodeURIComponent(range.from)}&to_date=${encodeURIComponent(range.to)}`;
  }
  return `range=${range}`;
}

/** Converts a MetricsRange into a react-query key suffix for cache isolation. */
export function rangeToKey(range: MetricsRange): string {
  if (isCustomDateRange(range)) {
    return `custom:${range.from}:${range.to}`;
  }
  return range;
}

// ---------------------------------------------------------------------------
// DateRangePicker bridge helpers
// ---------------------------------------------------------------------------

const PRESET_DAYS: Record<TimeRange, number> = { "7d": 7, "30d": 30, "90d": 90 };

function toISODate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function fromISODate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d, 0, 0, 0);
}

function isSameDay(a: Date, b: Date): boolean {
  return toISODate(a) === toISODate(b);
}

/** Converts a MetricsRange into a react-day-picker DateRange for the picker. */
export function metricsRangeToDateRange(range: MetricsRange): DateRange | undefined {
  if (isCustomDateRange(range)) {
    const from = fromISODate(range.from);
    const to = fromISODate(range.to);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return undefined;
    return { from, to };
  }
  const days = PRESET_DAYS[range];
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - (days - 1));
  return { from, to };
}

/** Converts a react-day-picker DateRange back into a MetricsRange.
 *
 * If the range matches a known preset (trailing N days ending today),
 * the preset string is returned for cleaner cache keys and backend semantics.
 * Otherwise a CustomDateRange with ISO date strings is produced.
 * Returns undefined when the range is incomplete (no `from` or no `to`).
 */
export function dateRangeToMetricsRange(range: DateRange | undefined): MetricsRange | undefined {
  if (!range?.from || !range.to) return undefined;
  const today = new Date();
  for (const [preset, days] of Object.entries(PRESET_DAYS) as [TimeRange, number][]) {
    const expectedFrom = new Date();
    expectedFrom.setDate(expectedFrom.getDate() - (days - 1));
    if (isSameDay(range.from, expectedFrom) && isSameDay(range.to, today)) {
      return preset;
    }
  }
  return { from: toISODate(range.from), to: toISODate(range.to) };
}

export interface PageViewPayload {
  page_path: string;
  entered_at: string;
  exited_at: string | null;
}

export interface AIUsagePoint {
  date: string;
  total_tokens: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  call_count: number;
}

export interface AIUsageByFeature {
  feature: string;
  total_tokens: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  call_count: number;
}

export interface AIUsageByModel {
  model: string;
  total_tokens: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  call_count: number;
}

export interface AIUsageRecentCall {
  feature: string;
  model: string;
  total_tokens: number;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  duration_ms: number;
  timestamp: number | null;
  user_id: string | null;
}

export interface AIUsageResponse {
  points: AIUsagePoint[];
  summary: {
    total_tokens: number;
    total_input_tokens: number;
    total_output_tokens: number;
    total_cost_usd: number;
    total_calls: number;
  };
  by_feature: AIUsageByFeature[];
  by_model: AIUsageByModel[];
  recent_calls: AIUsageRecentCall[];
}

export interface AIUsageRecentCallsResponse {
  calls: AIUsageRecentCall[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export interface AttributionRow {
  source: string;
  medium: string | null;
  campaign: string | null;
  ref: string | null;
  signups: number;
  active_trials: number;
  paid: number;
}

export interface AttributionResponse {
  rows: AttributionRow[];
  total_attributed_signups: number;
  total_paid: number;
}
